import { Injectable, NgZone } from '@angular/core';

import { AccountService } from '../../../../services/account.service';
import { SuburbAreasService } from '../../../../services/suburb-areas.service';
import { SuburbMapEngine } from '../map/suburb-map.engine';
import { SuburbCatalogueService } from './catalogue.service';
import { MapEditsService } from './map-edits.service';
import { PanelNavService } from './panel-nav.service';
import { WorkspaceStateService } from './workspace-state.service';

/** A salCode the popup may be opened on (data.focus). */
const SAL_CODE = /^\d{5,10}$/;

/**
 * The map's life in the workspace (#256): it is created on the workspace's elements with its events wired to the
 * workspace (an edit is saved, a click opens the suburb, the status and hover card follow), then loaded with the
 * regions, every stored assignment and the first state, and opened on a suburb when the popup was asked to.
 *
 * The map's events come from deck.gl and the basemap, outside Angular: they run in the zone and redraw the popup.
 */
@Injectable()
export class MapHostService {
  constructor(
    private zone: NgZone,
    private api: SuburbAreasService,
    private accountService: AccountService,
    private state: WorkspaceStateService,
    private catalogue: SuburbCatalogueService,
    private edits: MapEditsService,
    private nav: PanelNavService
  ) {}

  attach(mapEl: HTMLElement, lassoEl: HTMLCanvasElement): SuburbMapEngine {
    const inUi = (fn: () => void) =>
      this.zone.run(() => {
        fn();
        this.state.redraw();
      });
    const engine = new SuburbMapEngine(mapEl, lassoEl, {
      onAction: (edits, label, before) => inUi(() => this.edits.save(edits, label, before)),
      onStatus: (message, error) => inUi(() => this.state.setStatus(message, error)),
      onHover: (info) => inUi(() => (this.state.hover = info)),
      onChange: () => inUi(() => this.state.mapChanged()),
      onSelect: (code) => inUi(() => this.nav.openSuburb(code)),
    });
    engine.readOnly = this.state.readOnly;
    this.state.engine = engine;
    return engine;
  }

  /** Load everything the map needs and draw the first state; then the suburb asked for, if any. */
  async start(focus: string | undefined): Promise<void> {
    const state = this.state;
    const engine = state.engine;
    try {
      const [meta, rows] = await Promise.all([
        this.api.meta().toPromise(),
        this.api.allRows(),
        this.accountService.ensureFullAccount().toPromise(),
      ]);
      this.catalogue.apply(meta);
      engine.setData(
        this.catalogue.regions,
        rows,
        this.catalogue.pagesMap(),
        this.catalogue.subRegions
      );
      await this.zone.runOutsideAngular(() => engine.init());
      await this.zone.runOutsideAngular(() => engine.loadState(state.stateCode));
      if (focus && SAL_CODE.test(focus)) {
        await this.zone.runOutsideAngular(() => engine.flyToCode(focus));
        state.stateCode = engine.stateCode ?? state.stateCode;
        this.nav.openSuburb(focus);
      }
      state.loading = false;
      if (state.readOnly)
        state.setStatus('Read-only on this device: edit the suburb map on a desktop.');
    } catch (e: any) {
      state.loading = false;
      state.loadError = e?.message || String(e);
      state.setStatus(`The suburb map couldn't load: ${state.loadError}`, true);
    }
    state.redraw();
  }
}
